import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import type { LessonSession } from './types';
import styles from './ProgressCharts.module.css';

interface PassFailChartProps {
  sessions: LessonSession[];
}

const COLORS = {
  passed: '#22c55e',
  failed: '#ef4444',
};

export function PassFailChart({ sessions }: PassFailChartProps) {
  const { passed, failed, passRate } = useMemo(() => {
    const passedCount = sessions.filter((s) => s.passed).length;
    const failedCount = sessions.length - passedCount;
    const rate = sessions.length > 0 ? (passedCount / sessions.length) * 100 : 0;

    return { passed: passedCount, failed: failedCount, passRate: rate };
  }, [sessions]);

  const chartData = [
    { name: 'Passed', value: passed, color: COLORS.passed },
    { name: 'Failed', value: failed, color: COLORS.failed },
  ].filter((d) => d.value > 0);

  if (sessions.length === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>Pass Rate</h3>
        </div>
        <div className={styles.emptyState}>No data yet</div>
      </div>
    );
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>Pass Rate</h3>
        <p className={styles.chartSubtitle}>{sessions.length} sessions</p>
      </div>
      <div className={styles.donutContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={passed > 0 && failed > 0 ? 3 : 0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className={styles.donutCenter}>
          <span className={styles.donutValue}>{passRate.toFixed(0)}%</span>
          <span className={styles.donutLabel}>Pass</span>
        </div>
      </div>
      <div className={styles.passFailLegend}>
        <div className={styles.passFailItem}>
          <span className={styles.legendDot} style={{ backgroundColor: COLORS.passed }} />
          <span>Passed</span>
          <span className={styles.passFailCount}>{passed}</span>
        </div>
        <div className={styles.passFailItem}>
          <span className={styles.legendDot} style={{ backgroundColor: COLORS.failed }} />
          <span>Failed</span>
          <span className={styles.passFailCount}>{failed}</span>
        </div>
      </div>
    </div>
  );
}
